// web/src/App.jsx
import React, { useEffect, useState, useCallback } from "react";
import { BrowserRouter as Router, Route, Routes, Link } from "react-router-dom";
import { Helmet, HelmetProvider } from "react-helmet-async";
import AddressSearch from "./AddressSearch";
import ResultsPage from "./pages/ResultsPage";
import Compte from "./pages/Compte";
import VerifyEmail from "./pages/VerifyEmail";
import ResetPassword from "./pages/ResetPassword";
import AdminComptes from "./pages/AdminComptes";
import BrandPage from "./pages/BrandPage";
import CartesPage from "./pages/CartesPage";
import DemenagementHub from "./pages/demenagement/DemenagementHub";
import LettrePreavisLocation from "./pages/demenagement/LettrePreavisLocation";
import GuidePreavisLocation from "./pages/demenagement/GuidePreavisLocation";
import ChecklistDemenagement from "./pages/demenagement/ChecklistDemenagement";
import ChecklistVisiteLogement from "./pages/demenagement/ChecklistVisiteLogement";
import { GuidesIndex, GuideArticle } from "./pages/guides/GuidesPage";
import WidgetAddressPage from "./pages/WidgetAddressPage";
import WidgetPartnerPage from "./pages/WidgetPartnerPage";
import SharedReportPage from "./pages/SharedReportPage";
import GlobalUIFix from "./components/common/GlobalUIFix";
import SiteFooter from "./components/common/SiteFooter";
import RGPD from "./pages/RGPD";
import Conditions from "./pages/Conditions";
import HomeLoopVideo from "./components/common/HomeLoopVideo";
import logo from "./assets/logo-white-bg.png";

//import { ConsentProvider } from "./contexts/ConsentContext";
import { AuthProvider } from "./contexts/AuthContext";
import HeaderAuth from "./components/common/HeaderAuth";
import { LoginModal, SignupModal, ForgotModal } from "./components/common/AuthModals";

const EXAMPLE_ID = "example_full";

function AuthModalBus() {
  const [loginOpen, setLoginOpen] = useState(false);
  const [signupOpen, setSignupOpen] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);

  const openLogin = useCallback(() => {
    setSignupOpen(false);
    setForgotOpen(false);
    setLoginOpen(true);
  }, []);

  const openSignup = useCallback(() => {
    setLoginOpen(false);
    setForgotOpen(false);
    setSignupOpen(true);
  }, []);

  const openForgot = useCallback(() => {
    setLoginOpen(false);
    setSignupOpen(false);
    setForgotOpen(true);
  }, []);

  const closeAll = useCallback(() => {
    setLoginOpen(false);
    setSignupOpen(false);
    setForgotOpen(false);
  }, []);

  useEffect(() => {
    const onOpen = (e) => {
      const mode = e?.detail?.mode || "login";
      if (mode === "login") openLogin();
      else if (mode === "signup") openSignup();
      else if (mode === "forgot") openForgot();
    };
    const onClose = () => closeAll();

    window.addEventListener("open-auth-modal", onOpen);
    window.addEventListener("close-auth-modal", onClose);
    return () => {
      window.removeEventListener("open-auth-modal", onOpen);
      window.removeEventListener("close-auth-modal", onClose);
    };
  }, [openLogin, openSignup, openForgot, closeAll]);

  useEffect(() => {
    if (!loginOpen && !signupOpen && !forgotOpen) {
      try {
        document.body.style.overflow = "";
      } catch {}
    }
  }, [loginOpen, signupOpen, forgotOpen]);

  return (
    <>
      <LoginModal
        open={loginOpen}
        onClose={() => setLoginOpen(false)}
        onOpenSignup={openSignup}
        onOpenReset={openForgot}
      />
      <SignupModal
        open={signupOpen}
        onClose={() => setSignupOpen(false)}
        onOpenLogin={openLogin}
      />
      <ForgotModal open={forgotOpen} onClose={() => setForgotOpen(false)} />
    </>
  );
}

function Home() {
  const onOpenLogin = () =>
    window.dispatchEvent(new CustomEvent("open-auth-modal", { detail: { mode: "login" } }));
  const onOpenSignup = () =>
    window.dispatchEvent(new CustomEvent("open-auth-modal", { detail: { mode: "signup" } }));

  // === Canonical origin (préférence domaine racine sans www) ===
  const preferredOrigin = "https://etatdulieu.com";
  const runtimeOrigin =
    (typeof window !== "undefined" && window.location?.origin) || preferredOrigin;
  const canonicalOrigin = runtimeOrigin.includes("://www.etatdulieu.com")
    ? preferredOrigin
    : runtimeOrigin;

  const homeUrl = `${canonicalOrigin}/`;

  const donationUrl = "https://buy.stripe.com/7sYcN6cqu9ZF1vDfc80Ba00";

  // === JSON-LD: Organization / WebSite / WebApplication ===
  const orgJsonLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "État du Lieu",
    alternateName: ["Etat du Lieu", "Etat du Lieu - rapports d'adresse", "etatdulieu"],
    url: canonicalOrigin,
    logo: `${canonicalOrigin}/og/logo-512.png`,
    sameAs: [
      "https://www.facebook.com/share/19qrGeBBgm",
      "https://x.com/DuEtat86397?t=qgkP7DdM8gCRm671U991Sg&s=09",
      "https://www.linkedin.com/company/etat-du-lieu",
    ],
  };

  const siteJsonLd = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "État du Lieu",
    alternateName: ["Etat du Lieu", "Etat du Lieu - rapports d'adresse", "etatdulieu"],
    url: canonicalOrigin,
    inLanguage: "fr-FR",
  };

  const webappJsonLd = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "État du Lieu",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    url: canonicalOrigin,
  };

  return (
    <div className="home-container">
      <Helmet prioritizeSeoTags>
        <link rel="canonical" href={homeUrl} />
        <link rel="alternate" hrefLang="fr" href={homeUrl} />
        <link rel="alternate" hrefLang="x-default" href={homeUrl} />

        <meta name="robots" content="index,follow,max-image-preview:large" />
        <link rel="icon" type="image/png" href="/favicon.png" />

        <title>État du Lieu — Rapport d’adresse en France</title>
        <meta
          name="description"
          content="Cherchez une adresse en France et obtenez gratuitement un rapport clair : immobilier, météo & climat, services, transports, risques, et plus."
        />

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="État du Lieu" />
        <meta property="og:title" content="État du Lieu — Rapport d’adresse en France" />
        <meta
          property="og:description"
          content="Consultez gratuitement un rapport d’adresse en France : immobilier, météo, services, transports, risques et plus."
        />
        <meta property="og:url" content={homeUrl} />
        <meta property="og:image" content={`${canonicalOrigin}/og/home.png`} />
        <meta property="og:image:alt" content="État du Lieu — aperçu d’un rapport par adresse" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content="fr_FR" />
        <meta name="twitter:card" content="summary_large_image" />

        <script type="application/ld+json">{JSON.stringify(orgJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(siteJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(webappJsonLd)}</script>
      </Helmet>

      <a className="skip-link" href="#main">Aller au contenu</a>

      <style>{`
        :root{
          --bg1:#3b82f6; --bg2:#1e40af;
          --surface:rgba(255,255,255,0.95);
          --ink:#1e293b; --muted:#64748b; --white:#fff;
          --ring: rgba(59,130,246,0.5);
          --auth-reserve: 88px;
          --blue1:#3b82f6; --blue2:#1d4ed8;
          --green1:#22c55e; --green2:#16a34a;
          --amber1:#f59e0b; --amber2:#d97706;
        }
        @supports (padding: max(0px)){
          :root{ --auth-reserve: max(76px, calc(env(safe-area-inset-top, 0px) + 64px)); }
        }
        html[data-pdf="1"] { --auth-reserve: 0px !important; }
        html, body { margin: 0; padding: 0; color-scheme: light; }
        .home-container{
          min-height:100vh;
          background: linear-gradient(135deg,var(--bg1) 0%, var(--bg2) 100%);
          font-family: -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Oxygen,Ubuntu,Cantarell,sans-serif;
          color: var(--ink);
          position: relative;
        }
        .content-wrapper{
          max-width:1200px;
          margin:0 auto;
          padding: calc(60px + var(--auth-reserve)) 20px 60px;
          position:relative;
        }
        .skip-link{position:absolute;left:-9999px;top:auto;width:1px;height:1px;overflow:hidden;}
        .skip-link:focus{left:16px;top:16px;width:auto;height:auto;background:#111;color:#fff;padding:8px 12px;border-radius:8px;z-index:1000;}

        .hero-section{text-align:center;margin-bottom:80px;animation:fadeInUp .8s ease-out}
        .hero-title{
          font-size:clamp(2.5rem,5vw,4rem);
          font-weight:800;
          letter-spacing:-.02em;
          line-height:1.05;
          margin:0;
          background: linear-gradient(135deg,#fff 0%, #f8fafc 100%);
          -webkit-background-clip:text; background-clip:text; -webkit-text-fill-color:transparent;
        }
        .hero-subtitle{
          font-size:1.125rem; color:rgba(255,255,255,.92);
          max-width:700px; margin:16px auto 0; line-height:1.65;
        }
        .hero-title-wrap{ display:inline-flex; align-items:center; gap:16px; justify-content:center; }
        .hero-logo{ height:clamp(52px, 3em, 80px); width:auto; display:block; }

        .search-section{
          background:var(--surface); backdrop-filter:blur(20px);
          border-radius:24px; padding:40px; margin:0 auto 18px; max-width:800px;
          box-shadow:0 20px 40px rgba(0,0,0,.1); border:1px solid rgba(255,255,255,.2);
          animation:fadeInUp .8s ease-out .15s both; position: relative; z-index: 10;
        }
        .search-title{ font-size:1.35rem; font-weight:800; color:var(--ink); margin:0 0 20px; text-align:center; }

        .example-inline{
          display:flex;
          justify-content:center;
          flex-wrap:wrap;
          gap:10px;
          margin: 0 auto 30px;
          animation:fadeInUp .8s ease-out .18s both;
        }
        .example-inline-link{
          display:inline-flex;
          align-items:center;
          gap:10px;
          padding:12px 18px;
          border-radius:999px;
          text-decoration:none;
          font-weight:800;
          color:#fff;
          background:rgba(255,255,255,.14);
          border:1px solid rgba(255,255,255,.22);
          backdrop-filter:blur(16px);
          box-shadow:0 12px 28px rgba(0,0,0,.10);
          transition:transform .2s ease, box-shadow .2s ease, background .2s ease;
        }
        .example-inline-link:hover{
          transform:translateY(-2px);
          box-shadow:0 18px 34px rgba(0,0,0,.14);
          background:rgba(255,255,255,.18);
        }
        .example-inline-link.maps-link{
          padding:14px 22px;
          font-size:1.02rem;
          background:rgba(255,255,255,.20);
          border-color:rgba(255,255,255,.34);
        }

        .features-grid{
          display:grid;
          grid-template-columns:repeat(auto-fit,minmax(280px,1fr));
          gap:24px;
          margin-bottom:60px;
          position: relative;
          z-index: 0;
        }
        .feature-card{
          background:var(--surface); backdrop-filter:blur(20px); border-radius:20px; padding:28px;
          border:1px solid rgba(255,255,255,.2); box-shadow:0 10px 30px rgba(0,0,0,.08);
          transition:transform .25s ease, box-shadow .25s ease, border-color .25s ease;
          animation:fadeInUp .8s ease-out var(--delay, .2s) both;
        }
        .feature-card:hover{ transform:translateY(-6px); box-shadow:0 20px 40px rgba(0,0,0,.12); }
        .feature-icon{ font-size:2rem; margin-bottom:10px; display:block; }
        .feature-title{ font-size:1.125rem; font-weight:800; margin:0 0 8px; }
        .feature-text{ color:var(--muted); line-height:1.6; margin:0; font-size:.98rem; }

        .text-section{ position:relative; z-index:0; margin:0 0 60px; }
        .text-card .feature-title{ margin-bottom:10px; }
        .text-card .feature-text + .feature-text{ margin-top:8px; }

        .support-section{
          position: relative;
          overflow: hidden;
          text-align:center;
          background:
            radial-gradient(circle at 14% 18%, rgba(59,130,246,.16), transparent 32%),
            radial-gradient(circle at 86% 82%, rgba(245,158,11,.14), transparent 34%),
            linear-gradient(135deg, rgba(255,255,255,.96), rgba(255,255,255,.88));
          backdrop-filter:blur(22px);
          border-radius:24px;
          padding:34px 24px;
          border:1px solid rgba(255,255,255,.26);
          animation:fadeInUp .8s ease-out .20s both;
          margin:0 auto 34px;
          max-width:800px;
          box-shadow:
            0 24px 50px rgba(15,23,42,.12),
            inset 0 1px 0 rgba(255,255,255,.45);
        }
        .support-section::before{
          content:"";
          position:absolute;
          inset:0;
          pointer-events:none;
          background:
            linear-gradient(180deg, rgba(255,255,255,.28), rgba(255,255,255,0) 22%),
            linear-gradient(0deg, rgba(2,6,23,.04), rgba(2,6,23,0) 24%);
        }
        .support-title{
          position:relative;
          z-index:1;
          font-size:1.35rem;
          font-weight:800;
          color:var(--ink);
          margin:0 0 12px;
        }
        .support-text{
          position:relative;
          z-index:1;
          color:#334155;
          margin:0 auto 18px;
          line-height:1.7;
          max-width:640px;
        }
        .support-mini{
          position:relative;
          z-index:1;
          color:#64748b;
          font-size:.95rem;
          margin-top:14px;
        }

        .donate-link{
          display:inline-flex; align-items:center; gap:12px; padding:14px 30px; border-radius:999px;
          background:linear-gradient(135deg,var(--amber1),var(--amber2)); color:#fff; text-decoration:none; font-weight:800;
          box-shadow:0 10px 25px rgba(217,119,6,.28); transition:transform .2s ease, box-shadow .2s ease;
          outline:2px solid transparent; outline-offset:2px;
          position:relative;
          z-index:1;
        }
        .donate-link:hover{ transform:translateY(-2px); box-shadow:0 16px 35px rgba(217,119,6,.38); }

        .home-video-wrap{
          margin: 0 auto 56px;
          animation:fadeInUp .8s ease-out .24s both;
        }

        .home-video-section{
          position: relative;
          width: 100%;
          max-width: 560px;
          margin: 0 auto;
        }

        .home-video-glow{
          position: absolute;
          inset: -18px;
          border-radius: 40px;
          background:
            radial-gradient(circle at 18% 18%, rgba(59,130,246,.30), transparent 34%),
            radial-gradient(circle at 82% 74%, rgba(16,185,129,.18), transparent 38%),
            radial-gradient(circle at 50% 100%, rgba(255,255,255,.14), transparent 44%);
          filter: blur(34px);
          pointer-events: none;
          z-index: 0;
        }

        .home-video-frame{
          position: relative;
          z-index: 1;
          width: 100%;
          aspect-ratio: 1 / 1;
          overflow: hidden;
          border-radius: 32px;
          background: rgba(255,255,255,.28);
          border: 1px solid rgba(255,255,255,.30);
          box-shadow:
            0 28px 76px rgba(15,23,42,.18),
            0 12px 34px rgba(59,130,246,.08),
            inset 0 1px 0 rgba(255,255,255,.44);
          backdrop-filter: blur(22px);
          -webkit-backdrop-filter: blur(22px);
        }

        .home-video-frame::before{
          content:"";
          position:absolute;
          inset:0;
          pointer-events:none;
          border-radius:inherit;
          background:
            linear-gradient(180deg, rgba(255,255,255,.18), rgba(255,255,255,0) 20%),
            linear-gradient(0deg, rgba(2,6,23,.12), rgba(2,6,23,0) 24%);
          z-index:2;
        }

        .home-video-media{
          display:block;
          width:100%;
          height:100%;
          object-fit:cover;
          background:#dbe3ee;
        }

        .glass-chip{
          background: var(--surface);
          backdrop-filter: blur(18px);
          border: 1px solid rgba(255,255,255,0.22);
          border-radius: 14px;
          padding: 8px 10px;
          box-shadow: 0 12px 28px rgba(0,0,0,0.12);
          display: inline-flex;
          align-items:center;
          gap:10px;
        }
        .back-chip a{ text-decoration:none; color:var(--ink); font-weight:600; }

        .cities-section{ margin: 0 0 48px; }
        .cities-card{
          background: var(--surface);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255,255,255,.22);
          border-radius: 20px;
          padding: 22px;
          box-shadow: 0 16px 36px rgba(0,0,0,.12);
        }
        .cities-title{ margin: 0 0 8px; font-size: 1.35rem; font-weight: 900; }
        .cities-grid{ display:grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 10px; }
        .city-link{
          display:block;
          text-decoration:none;
          color:#0f172a;
          background:#fff;
          border:1px solid rgba(0,0,0,.08);
          border-radius:12px;
          padding:12px 14px;
          font-weight:800;
        }
        .city-link small{ display:block; font-weight:600; font-size:12px; color:#64748b; }
        .btn-inline{
          display:inline-flex;
          align-items:center;
          gap:8px;
          padding:10px 14px;
          border-radius:10px;
          font-weight:800;
          text-decoration:none;
          background:#f3f4f6;
          border:1px solid #e5e7eb;
          color:#0f172a;
        }

        @keyframes fadeInUp{
          from{opacity:0;transform:translateY(24px);}
          to{opacity:1;transform:translateY(0);}
        }

        @media (prefers-reduced-motion: reduce){
          .hero-section,
          .search-section,
          .example-inline,
          .feature-card,
          .support-section,
          .home-video-wrap{
            animation:none;
          }
          .feature-card,
          .donate-link,
          .example-inline-link{
            transition:none;
          }
        }

        @media (max-width: 768px){
          .search-section{
            padding:28px 20px;
            border-radius:20px;
          }

          .support-section{
            padding:28px 20px;
            border-radius:20px;
            margin-bottom:28px;
          }

          .home-video-wrap{
            margin: 0 auto 44px;
          }

          .home-video-section{
            max-width:min(100%, 460px);
          }

          .home-video-frame{
            border-radius:26px;
          }

          .home-video-glow{
            inset:-10px;
            filter:blur(24px);
          }
        }
      `}</style>

      <div className="content-wrapper" id="main" role="main" aria-label="Accueil" style={{ position: "relative" }}>
        <HeaderAuth onOpenLogin={onOpenLogin} onOpenSignup={onOpenSignup} floating top={12} right={12} />

        <section className="hero-section">
          <div className="hero-title-wrap">
            <img src={logo} alt="État du Lieu" className="hero-logo" />
            <h1 className="hero-title">État du Lieu</h1>
          </div>
          <p className="hero-subtitle">
            <br />
            <strong>Avant d'acheter, de louer ... Faites un état des lieux du quartier !</strong>
            <br />
            <br />
            Découvrez tout ce qu’il faut savoir sur n’importe quelle adresse en France métropolitaine :
            prix immobiliers, météo & climat, services, transports, risques…
            <br />
            Gagnez du temps dans vos recherches !
            <br />
          </p>
        </section>

        <section className="search-section" aria-labelledby="search-title" id="search">
          <h2 id="search-title" className="search-title">Commencez votre recherche</h2>
          <AddressSearch />
        </section>

        <div className="example-inline" aria-label="Exemple interactif">
          <Link
            className="example-inline-link"
            to={{ pathname: `/results/${encodeURIComponent(EXAMPLE_ID)}`, search: `?example=1` }}
          >
            ✨ Voir un exemple interactif
          </Link>
          <Link className="example-inline-link maps-link" to="/cartes">
            🗺️ Voir les cartes de France
          </Link>
        </div>

        <section className="support-section" aria-label="Soutenir le projet">
          <h2 className="support-title">Vous trouvez le site utile ?</h2>
          <p className="support-text">
            Le site est gratuit pour tout le monde. Si l’outil vous aide dans vos recherches,
            vous pouvez soutenir son développement et son hébergement avec un don.
          </p>
          <div style={{ display: "flex", justifyContent: "center", flexWrap: "wrap" }}>
            <a
              className="donate-link"
              href={donationUrl}
              target="_blank"
              rel="noreferrer"
            >
              ❤️ Faire un don
            </a>
          </div>
          <div className="support-mini">
            Chaque contribution aide à garder le service gratuit et à continuer les améliorations.
          </div>
        </section>

        <div className="home-video-wrap">
          <HomeLoopVideo />
        </div>

        <section className="features-grid" aria-label="Fonctionnalités">
          <article className="feature-card" style={{ "--delay": "0.15s" }}>
            <span className="feature-icon">📁</span>
            <h3 className="feature-title">Rapport complet : données</h3>
            <p className="feature-text">
              Prix immobiliers, risques, projections climatiques, profils socio-démographiques, et plus encore dans un même rapport.
            </p>
          </article>
          <article className="feature-card" style={{ "--delay": "0.25s" }}>
            <span className="feature-icon">🗺️</span>
            <h3 className="feature-title">Rapport complet : cartes</h3>
            <p className="feature-text">
              Cartes interactives des alentours : services, transports, écoles, loisirs… pour visualiser le quotidien autour de l’adresse.
            </p>
          </article>
          <article className="feature-card" style={{ "--delay": "0.35s" }}>
            <span className="feature-icon">🔒</span>
            <h3 className="feature-title">Sources publiques</h3>
            <p className="feature-text">
              Toutes les données utilisées sont publiques, traitées de manière robuste, sans IA générative, et toutes les sources sont disponibles dans le rapport.
            </p>
          </article>
          <article className="feature-card" style={{ "--delay": "0.45s" }}>
            <span className="feature-icon">📊</span>
            <h3 className="feature-title">Synthèse claire</h3>
            <p className="feature-text">
              Un maximum d’informations utiles dans un format lisible, actionnable, prêt à partager, et optimisé aussi sur mobile.
            </p>
          </article>
          <article className="feature-card" style={{ "--delay": "0.55s" }}>
            <span className="feature-icon">⚡</span>
            <h3 className="feature-title">Mises à jour régulières</h3>
            <p className="feature-text">
              Données rafraîchies fréquemment : hebdomadaire pour les plus dynamiques, annuelle ou pluriannuelle pour les référentiels stables.
            </p>
          </article>
          <article className="feature-card" style={{ "--delay": "0.65s" }}>
            <span className="feature-icon">🇫🇷</span>
            <h3 className="feature-title">Hébergement en France</h3>
            <p className="feature-text">
              Infrastructure hébergée chez OVHcloud, pour une localisation claire des données et un hébergement souverain.
            </p>
          </article>
        </section>

        <section className="text-section" aria-label="Mentions importantes">
          <article className="feature-card text-card" style={{ "--delay": "0.18s" }}>
            <h3 className="feature-title">Mentions importantes</h3>
            <p className="feature-text">
              Certaines données sont partielles ou absentes pour les adresses hors France métropolitaine :
              météo, projections climatiques, services, transports, ainsi que d’autres indicateurs. L’usage du service est
              donc déconseillé pour ces adresses.
            </p>
            <p className="feature-text">
              Les données issues du fichier Demande de Valeur Foncière (DVF) ne couvrent pas l’Alsace-Moselle :
              informations immobilières indisponibles pour les départements 57, 67 et 68.
            </p>
          </article>
        </section>

        <section className="cities-section" aria-label="Aperçu par ville">
          <div className="cities-card">
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
              <h2 className="cities-title" style={{ margin: 0 }}>Aperçu par ville</h2>
              <a className="btn-inline" href="/villes/">Toutes les villes →</a>
            </div>

            <div className="cities-grid" style={{ marginTop: 12 }}>
              <a className="city-link" href="/villes/paris-01/"><strong>Paris 1er</strong></a>
              <a className="city-link" href="/villes/marseille-1er/"><strong>Marseille 1er</strong></a>
              <a className="city-link" href="/villes/lyon-1er/"><strong>Lyon 1er</strong></a>

              <a className="city-link" href="/villes/bordeaux/"><strong>Bordeaux</strong></a>
              <a className="city-link" href="/villes/toulouse/"><strong>Toulouse</strong></a>
              <a className="city-link" href="/villes/nice/"><strong>Nice</strong></a>
              <a className="city-link" href="/villes/nantes/"><strong>Nantes</strong></a>
              <a className="city-link" href="/villes/montpellier/"><strong>Montpellier</strong></a>
              <a className="city-link" href="/villes/lille/"><strong>Lille</strong></a>
              <a className="city-link" href="/villes/rennes/"><strong>Rennes</strong></a>
              <a className="city-link" href="/villes/reims/"><strong>Reims</strong></a>
              <a className="city-link" href="/villes/saint-etienne/"><strong>Saint-Étienne</strong></a>
              <a className="city-link" href="/villes/toulon/"><strong>Toulon</strong></a>
              <a className="city-link" href="/villes/grenoble/"><strong>Grenoble</strong></a>
              <a className="city-link" href="/villes/dijon/"><strong>Dijon</strong></a>
              <a className="city-link" href="/villes/angers/"><strong>Angers</strong></a>
              <a className="city-link" href="/villes/nimes/"><strong>Nîmes</strong></a>
              <a className="city-link" href="/villes/villeurbanne/"><strong>Villeurbanne</strong></a>
              <a className="city-link" href="/villes/le-havre/"><strong>Le Havre</strong></a>
              <a className="city-link" href="/villes/versailles/"><strong>Versailles</strong></a>
            </div>
          </div>
        </section>

        <SiteFooter />
      </div>
    </div>
  );
}

export default function App() {
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 640px)");
    const setMeta = (name, content) => {
      let el = document.querySelector(`meta[name="\${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    const applyLight = () => {
      if (!mq.matches) return;
      try {
        document.documentElement.style.colorScheme = "light";
      } catch {}
      setMeta("color-scheme", "light");
      setMeta("supported-color-schemes", "light");
      setMeta("theme-color", "#ffffff");
      setMeta("apple-mobile-web-app-status-bar-style", "default");
    };
    applyLight();
    try {
      mq.addEventListener("change", applyLight);
    } catch {}
    return () => {
      try {
        mq.removeEventListener("change", applyLight);
      } catch {}
    };
  }, []);

  const widgetOnly =
    typeof window !== "undefined" && window.location.pathname === "/widget/adresse";

  if (widgetOnly) {
    return (
      <HelmetProvider>
        <Router>
          <Routes>
            <Route path="/widget/adresse" element={<WidgetAddressPage />} />
          </Routes>
        </Router>
      </HelmetProvider>
    );
  }

  return (
    <AuthProvider>
      <HelmetProvider>
        <Router>
          <GlobalUIFix />
          <AuthModalBus />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/results/:id" element={<ResultsPage />} />
            <Route path="/compte" element={<Compte />} />
            <Route path="/verify" element={<VerifyEmail />} />
            <Route path="/v/:token" element={<VerifyEmail />} />
            <Route path="/reset" element={<ResetPassword />} />
            <Route path="/admin/comptes" element={<AdminComptes />} />
            <Route path="/rgpd" element={<RGPD />} />
            <Route path="/conditions" element={<Conditions />} />
            <Route path="/etat-du-lieu" element={<BrandPage />} />
            <Route path="/cartes" element={<CartesPage />} />
            <Route path="/demenagement" element={<DemenagementHub />} />
            <Route path="/guides" element={<GuidesIndex />} />
            <Route path="/guides/:slug" element={<GuideArticle />} />
            <Route path="/outils/lettre-preavis-location" element={<LettrePreavisLocation />} />
            <Route path="/guides/preavis-location-1-mois-ou-3-mois" element={<GuidePreavisLocation />} />
            <Route path="/checklists/checklist-demenagement-pdf" element={<ChecklistDemenagement />} />
            <Route path="/checklists/checklist-visite-logement" element={<ChecklistVisiteLogement />} />
            <Route path="/widget/adresse" element={<WidgetAddressPage />} />
            <Route path="/partenaires/widget" element={<WidgetPartnerPage />} />
            <Route path="/partage/:token" element={<SharedReportPage />} />
          </Routes>
        </Router>
      </HelmetProvider>
    </AuthProvider>
  );
}
